import { useState, useCallback } from "react";
import { CustomModal, Button } from "../../shared";
import { HiOutlineFolder, HiOutlineChevronRight, HiOutlineDocument } from "react-icons/hi2";
import { cn } from "../../../lib/cn";

const FilePickerNode = ({ node, level = 0, selectedFileId, onSelectFile, onNavigateFolder }) => {
  const [expanded, setExpanded] = useState(true);
  const childFolders = node.children || [];
  const childFiles = node.files || [];
  const hasContents = childFolders.length > 0 || childFiles.length > 0;

  return (
    <div className="text-sm">
      <div
        className={cn(
          "flex items-center gap-2 rounded-lg px-2 py-1.5 transition-colors hover:bg-surface-hover",
        )}
        style={{ paddingLeft: `${level * 16 + 8}px` }}
      >
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className={cn(
            "flex h-5 w-5 items-center justify-center rounded text-grey transition-transform",
            hasContents ? "opacity-100" : "invisible",
            expanded && "rotate-90",
          )}
          aria-label={expanded ? "Collapse" : "Expand"}
          aria-expanded={expanded}
        >
          <HiOutlineChevronRight className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={() => onNavigateFolder?.(node)}
          className="flex flex-1 items-center gap-2 text-left"
        >
          <HiOutlineFolder className="h-4 w-4 text-secondary-dark" />
          <span className="truncate font-medium text-darkgrey">{node.name}</span>
        </button>
      </div>
      {expanded && hasContents && (
        <div className="ml-3 border-l border-surface-border pl-1">
          {childFolders.map((child) => (
            <FilePickerNode
              key={child.id}
              node={child}
              level={level + 1}
              selectedFileId={selectedFileId}
              onSelectFile={onSelectFile}
              onNavigateFolder={onNavigateFolder}
            />
          ))}
          {childFiles.map((file) => (
            <button
              key={`file-${file.id}`}
              type="button"
              onClick={() => onSelectFile?.(file)}
              className={cn(
                "flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left transition-colors",
                selectedFileId === file.id
                  ? "bg-secondary/10 ring-2 ring-secondary/60"
                  : "hover:bg-surface-hover",
              )}
              style={{ paddingLeft: `${(level + 1) * 16 + 8}px` }}
            >
              <span className="h-5 w-5 shrink-0" aria-hidden="true" />
              <HiOutlineDocument className="h-4 w-4 shrink-0 text-grey" aria-hidden="true" />
              <span className={cn("truncate", selectedFileId === file.id ? "text-secondary-dark font-medium" : "text-grey")}>
                {file.name}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const FilePickerModal = ({
  open,
  onClose,
  tree,
  selectedFileId,
  onSelectFile,
  onAttach,
  isLoading = false,
  title = "Select file",
  attachLabel = "Attach file",
}) => {
  const handleAttach = useCallback(() => {
    if (selectedFileId && onAttach) {
      onAttach(selectedFileId);
    }
  }, [selectedFileId, onAttach]);

  return (
    <CustomModal
      title={title}
      open={open}
      close={onClose}
      showPrimaryButton={false}
      showSecondaryButton={false}
      dialogSize="lg"
    >
      <div className="flex flex-col gap-4">
        {!tree ? (
          <p className="text-sm text-grey py-8 text-center">No files available.</p>
        ) : (
          <div className="max-h-72 overflow-y-auto rounded-xl border border-surface-border bg-white p-2">
            <FilePickerNode
              node={tree}
              level={0}
              selectedFileId={selectedFileId}
              onSelectFile={onSelectFile}
            />
          </div>
        )}

        <div className="flex items-center justify-end gap-3 pt-2">
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="filled"
            color="primary"
            onClick={handleAttach}
            loading={isLoading}
            disabled={!selectedFileId || isLoading}
          >
            {attachLabel}
          </Button>
        </div>
      </div>
    </CustomModal>
  );
};

export default FilePickerModal;
